import {
  FiTarget,
  FiFileText,
  FiUpload,
  FiHeart,
  FiActivity,
  FiUsers,
  FiClipboard,
  FiBriefcase,
  FiShield,
  FiCalendar,
  FiShare2,
  FiTrendingUp,
  FiBarChart,
  FiMessageSquare,
} from 'react-icons/fi'

export interface UseCaseStep {
  number: number
  title: string
  description: string
}

export interface UseCaseExample {
  title: string
  conversation: string[]
}

export interface UseCase {
  id: string
  title: string
  description: string
  icon: typeof FiTarget
  whyMatters: string
  steps: UseCaseStep[]
  examples: UseCaseExample[]
  order: number
  category: 'individual' | 'caregiver' | 'coach'
  imagePlaceholder?: string
}

export const useCases: UseCase[] = [
  {
    id: 'wellness-goals',
    title: 'Set & Achieve Wellness Goals with Coach',
    description: 'Create and track personalized wellness goals with structured accountability from Wellness Coach',
    icon: FiTarget,
    category: 'individual',
    whyMatters: 'Goals informed by your genetics and health data are more likely to succeed. Wellness Coach provides structured accountability and adjusts strategies based on your progress.',
    imagePlaceholder: 'Individual setting wellness goals',
    steps: [
      {
        number: 1,
        title: 'Create Your Goal',
        description: 'Go to Wellness Goals → Create New Goal, or ask Wellness Coach to help you create a goal. Set specific metrics, target date, and category.',
      },
      {
        number: 2,
        title: 'Initial Check-In with Wellness Coach',
        description: 'Go to Wellness Coach → Start a goal check-in session. Coach asks about your starting point and documents baseline metrics. Schedule regular check-ins (weekly, bi-weekly, or monthly).',
      },
      {
        number: 3,
        title: 'Regular Progress Check-Ins',
        description: 'Coach asks focused questions: "How did your sleep go this week?" Share progress, challenges, and observations. Coach tracks progress, identifies patterns, suggests adjustments, and celebrates wins.',
      },
      {
        number: 4,
        title: 'Photo Documentation (Optional)',
        description: 'For nutrition goals: share meal photos. For fitness goals: share progress photos. Coach analyzes and provides feedback, saving insights to progress notes.',
      },
      {
        number: 5,
        title: 'Adjust and Optimize',
        description: 'If you\'re not making progress, tell Wellness Coach. It analyzes barriers and suggests: adjusting timeline, breaking into smaller steps, changing approach, or addressing obstacles.',
      },
    ],
    examples: [
      {
        title: 'Example Conversation Flow',
        conversation: [
          'Coach: "How did your sleep go this week?"',
          'You: "Pretty good, I got 7 hours most nights"',
          'Coach: "That\'s great progress! What time did you typically go to bed?"',
          'You: "Around 10:30 PM"',
          'Coach: "Consistency is key for your CLOCK variant. How did you feel in the mornings compared to last week?"',
        ],
      },
    ],
    order: 1,
  },
  {
    id: 'health-intake',
    title: 'Organize Your Health Information Through Structured Intake',
    description: 'Build a complete health profile through guided Wellness Coach intake sessions',
    icon: FiFileText,
    category: 'individual',
    whyMatters: 'A complete health profile enables personalized recommendations and better healthcare conversations. Wellness Coach guides you through structured intake to capture all important health information.',
    imagePlaceholder: 'Health intake session',
    steps: [
      {
        number: 1,
        title: 'Start Wellness Coach Intake Session',
        description: 'Go to Wellness Coach → Start new session. Coach guides you through your health information with structured questions.',
      },
      {
        number: 2,
        title: 'Add Your Health Records',
        description: 'Coach asks structured questions: Conditions ("What health conditions are you managing?"), Medications ("What medications are you taking?"), Supplements, Allergies, and Recent concerns. Coach automatically creates records, checks for interactions, and suggests relevant wellness goals.',
      },
      {
        number: 3,
        title: 'Upload Lab Results',
        description: 'Upload recent lab results via Documents or drag into Wellness Coach. Coach analyzes and creates lab result records, compares values to reference ranges, and identifies trends.',
      },
      {
        number: 4,
        title: 'Schedule Follow-Up Sessions',
        description: 'After intake, Coach offers to schedule follow-ups. Choose weekly, bi-weekly, or monthly. Use follow-ups to update medications, track symptom changes, review progress on goals, and discuss new health concerns.',
      },
    ],
    examples: [
      {
        title: 'Example Conversation Flow',
        conversation: [
          'Coach: "What medications are you currently taking?"',
          'You: "I take levothyroxine 50mcg daily for my thyroid"',
          'Coach: "I\'ve added that to your treatments. How long have you been taking it?"',
          'You: "About 6 months"',
          'Coach: "How effective has it been for managing your symptoms?"',
          '[Coach continues gathering information and checking for interactions]',
        ],
      },
    ],
    order: 2,
  },
  {
    id: 'genetic-upload',
    title: 'Upload and Understand Your Genetic Data',
    description: 'Order a Livewello DNA kit or upload existing genetic data to get personalized insights about your unique genetic profile',
    icon: FiUpload,
    category: 'individual',
    whyMatters: 'Your genetic data unlocks personalized health insights that generic advice can\'t provide. Understanding your genetic variants helps you make informed decisions about nutrition, supplements, medications, and lifestyle.',
    imagePlaceholder: 'Genetic data upload',
    steps: [
      {
        number: 1,
        title: 'Get Your Genetic Data',
        description: 'Order a Livewello DNA kit at /dna-kit for the best coverage. Already tested? Download your raw data file from 23andMe, AncestryDNA, or another provider (.txt, .zip, or .vcf).',
      },
      {
        number: 2,
        title: 'Upload to LiveWello',
        description: 'Go to Genotype Reports → Click "Upload Raw Data". Select your file and wait for processing (usually a few minutes to an hour). You\'ll see confirmation when processing completes.',
      },
      {
        number: 3,
        title: 'Explore Your Genetic Reports',
        description: 'Navigate to Genotype Reports to view comprehensive genetic analysis. Browse different health categories: nutrition, fitness, medication response, and more.',
      },
      {
        number: 4,
        title: 'Ask Questions About Your Genetics',
        description: 'Use Wellness Coach to ask specific questions: "What do my MTHFR variants mean?" or "How do my genetics affect vitamin D needs?" Get personalized answers based on your specific genetic profile.',
      },
    ],
    examples: [
      {
        title: 'Example Questions to Ask',
        conversation: [
          '"What do my MTHFR variants mean for folate supplementation?"',
          '"Based on my genetics, what vitamins should I focus on?"',
          '"Do I have any variants that affect vitamin D metabolism?"',
          '"What supplements would be most beneficial for my genetic profile?"',
        ],
      },
    ],
    order: 3,
  },
  {
    id: 'nutrition-supplements',
    title: 'Optimize Nutrition & Supplements Based on Genetics',
    description: 'Choose supplements and foods that work with your genetic variants for optimal health',
    icon: FiHeart,
    category: 'individual',
    whyMatters: 'Your genetics affect how you process nutrients. This workflow helps you choose supplements and foods that work with your genetic variants, avoiding wasted money on ineffective supplements and optimizing your health outcomes.',
    imagePlaceholder: 'Nutrition and supplements',
    steps: [
      {
        number: 1,
        title: 'Get Your Genetic Data',
        description: 'If you haven\'t already, order a Livewello DNA kit or upload existing raw data from 23andMe, AncestryDNA, or another provider. Go to Genotype Reports → Upload Raw Data.',
      },
      {
        number: 2,
        title: 'Ask Wellness Coach About Your Genetics',
        description: 'Open Coach and ask questions like: "What do my MTHFR variants mean for folate supplementation?" or "Based on my genetics, what vitamins should I focus on?" Wellness Coach analyzes your variants and suggests specific supplements and dosages.',
      },
      {
        number: 3,
        title: 'Create Supplement Tracking Records',
        description: 'When Wellness Coach suggests a supplement, it offers to add it to your supplement list. Say "yes" and it creates a record with supplement name, dosage, genetic rationale, timing recommendations, and expected benefits.',
      },
      {
        number: 4,
        title: 'Track Effectiveness Over Time',
        description: 'Add lab results: Upload vitamin level labs (B12, D, folate, etc.). Wellness Coach compares results to your genetics and suggests adjustments. Create wellness goals to track improvements. Use Wellness Coach for regular check-ins on energy, mood, and sleep.',
      },
      {
        number: 5,
        title: 'Optimize Based on Results',
        description: 'Upload follow-up labs. Wellness Coach analyzes trends and genetic factors. Adjust dosages or timing as needed. Document what works in your health records.',
      },
    ],
    examples: [
      {
        title: 'Example Conversation Flow',
        conversation: [
          'You: "I\'ve been feeling tired lately, and my B12 was low on my last test"',
          'Coach: "Thanks for sharing. I see you have an MTRR variant that may increase your B12 needs. Are you currently supplementing?"',
          'You: "No, not yet"',
          'Coach: "Given your genetics, methylcobalamin (methylated B12) may be more effective. Would you like me to add B12 supplementation to your treatments list and create a wellness goal to track energy levels?"',
          '[You agree, Wellness Coach creates records and goals]',
        ],
      },
    ],
    order: 4,
  },
  {
    id: 'chronic-conditions',
    title: 'Manage Chronic Conditions with Genetics-Informed Care',
    description: 'Track conditions, medications, and lab results with personalized genetic insights',
    icon: FiActivity,
    category: 'individual',
    whyMatters: 'Genetics can affect medication response, nutrient needs, and condition management. This workflow helps you track everything in one place and get personalized guidance based on your unique genetic profile.',
    imagePlaceholder: 'Chronic condition management',
    steps: [
      {
        number: 1,
        title: 'Set Up Your Health Profile with Wellness Coach',
        description: 'Go to Wellness Coach → Start new session. Add your condition: "I have hypothyroidism". Add current medications: "I take levothyroxine 50mcg daily". Coach adds records, checks for genetic interactions, and suggests tracking effectiveness.',
      },
      {
        number: 2,
        title: 'Upload Lab Results',
        description: 'Upload recent labs via Documents or drag into Coach. Wellness Coach analyzes: extracts values and dates, compares to reference ranges, links to your conditions, identifies trends, and offers to create lab result records.',
      },
      {
        number: 3,
        title: 'Get Personalized Recommendations',
        description: 'Ask Wellness Coach questions like: "How do my genetics affect my thyroid medication?" or "What supplements should I avoid with my current medications?" Wellness Coach reviews your genetics, conditions, medications, and labs to provide personalized insights.',
      },
      {
        number: 4,
        title: 'Track Progress with Wellness Coach',
        description: 'Schedule follow-up sessions. Regular check-ins: Coach asks "How has your energy been since starting the new medication?" You share updates. Coach adds notes to your treatment record and suggests adjustments.',
      },
      {
        number: 5,
        title: 'Prepare for Healthcare Provider Visits',
        description: 'Ask Wellness Coach: "Prepare a summary for my doctor appointment next week." It provides current conditions, medications, recent lab results with trends, genetic factors relevant to your care, and questions to discuss.',
      },
    ],
    examples: [
      {
        title: 'Example Conversation Flow',
        conversation: [
          '[In Wellness Coach intake session]',
          'Coach: "What medications are you currently taking?"',
          'You: "I take Imitrex for my migraines"',
          'Coach: "Imitrex (sumatriptan) can be effective for migraine relief. How long have you been managing migraines?"',
          'You: "About 2 years"',
          'Coach: "Would you like me to add Imitrex to your treatments list and check for any genetic factors that might relate to migraine patterns?"',
        ],
      },
    ],
    order: 5,
  },
  {
    id: 'family-health',
    title: 'Manage Family Health Across Multiple Profiles',
    description: 'Track health for yourself and family members, identify patterns, and get personalized guidance for each person',
    icon: FiUsers,
    category: 'individual',
    whyMatters: 'Many people manage health for multiple family members. This workflow helps you track everyone\'s health in one place, identify family health patterns, and get personalized guidance for each person.',
    imagePlaceholder: 'Family health management',
    steps: [
      {
        number: 1,
        title: 'Create Family Member Profiles',
        description: 'Go to Profiles → Add Profile. Create profiles for each family member. Include name, relationship, date of birth, and gender. Use clear names like "Mom - Sarah" or "Dad - John".',
      },
      {
        number: 2,
        title: 'Upload Genetic Data for Each Person',
        description: 'Switch to each profile. Go to Genotype Reports → Upload their genetic data. Wait for processing. Each profile processes independently.',
      },
      {
        number: 3,
        title: 'Build Individual Health Records',
        description: 'For each family member: Switch to their profile. Go to Wellness Coach → Start new session. Coach guides through their health information: conditions, medications, supplements, allergies, and health concerns.',
      },
      {
        number: 4,
        title: 'Set Goals for Each Family Member',
        description: 'Switch to each profile. Ask Wellness Coach: "Help [name] create a goal for better sleep." Wellness Coach creates it in their profile. Use Wellness Coach for structured check-ins per person.',
      },
      {
        number: 5,
        title: 'Identify Family Health Patterns',
        description: 'Ask Wellness Coach: "What genetic variants do my family members share?" Wellness Coach identifies common variants and explains family health implications. Review conditions across profiles to identify patterns.',
      },
    ],
    examples: [
      {
        title: 'Example Conversation Flow',
        conversation: [
          'You: "I want to help my mom manage her health. She has type 2 diabetes and takes metformin"',
          '[Switch to mom\'s profile]',
          'Coach: "I\'d be happy to help! I\'ve added type 2 diabetes to her conditions and metformin to her treatments. Given that she\'s taking metformin, we should check for MTHFR variants - metformin can affect B12 absorption. Would you like me to check her genetics and create a goal to monitor her blood sugar?"',
        ],
      },
    ],
    order: 6,
  },
  {
    id: 'document-management',
    title: 'Document Management and Lab Result Analysis',
    description: 'Upload, organize, and analyze health documents and lab results',
    icon: FiClipboard,
    category: 'individual',
    whyMatters: 'Health documents and lab results contain critical information, but they\'re often scattered and hard to analyze. This workflow helps you centralize all documents, extract key information, and track trends over time.',
    imagePlaceholder: 'Document management',
    steps: [
      {
        number: 1,
        title: 'Upload Documents',
        description: 'Go to Documents → Upload button (or drag and drop). Supported formats: PDFs, images of lab reports, medical records, genetic reports, and other health documents.',
      },
      {
        number: 2,
        title: 'Automatic Analysis',
        description: 'System automatically classifies document type, extracts text using OCR, and organizes documents. You can edit classification and tags if needed.',
      },
      {
        number: 3,
        title: 'Analyze with Wellness Coach',
        description: 'Upload documents to Coach or ask: "Can you review the lab results I just uploaded?" Wellness Coach analyzes: extracts values and dates, compares to reference ranges, links to your conditions, identifies trends, and offers to create health records.',
      },
      {
        number: 4,
        title: 'Track Trends Over Time',
        description: 'View all lab results in Health Records. Wellness Coach can compare current results to previous ones: "How do these lab results compare to my previous tests?" or "Has my cholesterol improved since last time?"',
      },
      {
        number: 5,
        title: 'Organize and Search',
        description: 'Use Documents page to search and filter by type, date, or keywords. Add custom tags for organization. Link related documents together.',
      },
    ],
    examples: [
      {
        title: 'Example Conversation Flow',
        conversation: [
          'You: [Uploads lab result PDF]',
          'Coach: "I\'ve analyzed your lab results. I found: TSH 4.2 mIU/L (slightly elevated), B12 350 pg/mL (low-normal). Would you like me to create lab result records for these values?"',
          'You: "Yes, please"',
          'Coach: "I\'ve created records for your TSH and B12 results. Your TSH is slightly elevated - this might relate to your hypothyroidism. Would you like me to check if your genetics affect thyroid function?"',
        ],
      },
    ],
    order: 7,
  },
  {
    id: 'provider-prep',
    title: 'Prepare Health Information for Healthcare Providers',
    description: 'Organize comprehensive health summaries and talking points for doctor visits',
    icon: FiBriefcase,
    category: 'individual',
    whyMatters: 'Having organized, genetics-informed health data helps you and your healthcare provider make better decisions. This workflow helps you prepare comprehensive information for appointments and have productive conversations.',
    imagePlaceholder: 'Preparing for doctor visit',
    steps: [
      {
        number: 1,
        title: 'Organize All Your Health Data',
        description: 'Use Wellness Coach for structured intake to ensure all information is captured: conditions, medications, supplements, allergies, and lab results. Upload genetic data if not already done.',
      },
      {
        number: 2,
        title: 'Generate Pre-Visit Summary',
        description: 'Ask Wellness Coach: "Prepare a summary for my doctor appointment next week." It provides: current conditions and medications, recent lab results with trends, genetic factors relevant to your care, supplement list with dosages, questions to discuss, and concerns or changes since last visit.',
      },
      {
        number: 3,
        title: 'Review Genetic Insights',
        description: 'Ask Wellness Coach: "What genetic factors should my doctor know about?" or "Are there any medication interactions based on my genetics?" Get pharmacogenomics insights, nutrient needs based on genetics, and genetic predispositions relevant to your conditions.',
      },
      {
        number: 4,
        title: 'Create Talking Points',
        description: 'Wellness Coach helps you prepare: Medication questions ("Based on my CYP2D6 variant, should we adjust my medication dosage?"), Testing recommendations ("Given my MTHFR variant, should we monitor homocysteine levels?"), and Treatment optimization questions.',
      },
      {
        number: 5,
        title: 'Access During Your Visit',
        description: 'Open LiveWello on your phone for quick access to all records. Reference specific lab results, discuss genetic insights, and show medication lists. After visit, tell Wellness Coach about changes: "My doctor increased my medication to 75mcg" - it updates your records.',
      },
    ],
    examples: [
      {
        title: 'Example Conversation Flow',
        conversation: [
          'You: "I have a doctor appointment next week. Help me prepare"',
          'Coach: "I\'ll help you get ready! Let me review your health profile and create a comprehensive summary."',
          '[Wellness Coach reviews your records]',
          'Coach: "Here\'s your pre-visit summary: Current Status: Conditions: Hypothyroidism, Medications: Levothyroxine 50mcg daily, Recent Labs: TSH 4.2 (slightly elevated). Genetic Factors to Discuss: MTHFR C677T variant may affect folate metabolism. Questions to Consider: Should we increase levothyroxine given current TSH?"',
        ],
      },
    ],
    order: 8,
  },
  // Caregiver use cases
  {
    id: 'shared-health-records',
    title: 'Create and Manage Shared Health Records',
    description: 'Build comprehensive health profiles for loved ones with fine-grained sharing permissions',
    icon: FiShield,
    category: 'caregiver',
    whyMatters: 'Caregivers need to organize health information for someone else while respecting privacy. Livewello lets you create profiles, manage records, and share selected information with doctors, family members, or coaches — all with granular permissions.',
    imagePlaceholder: 'Shared health records',
    steps: [
      {
        number: 1,
        title: 'Create Profile for Loved One',
        description: 'Go to Profiles → Add Profile. Create a profile with their name, date of birth, and relationship. You can manage multiple profiles from one account.',
      },
      {
        number: 2,
        title: 'Set Up Health Records',
        description: 'Switch to their profile. Use Wellness Coach to add conditions, medications, supplements, allergies, and health concerns. Coach guides you through structured intake.',
      },
      {
        number: 3,
        title: 'Upload Their Documents',
        description: 'Upload lab results, medical records, and genetic data to their profile. Wellness Coach analyzes documents and creates health records automatically.',
      },
      {
        number: 4,
        title: 'Configure Sharing Permissions',
        description: 'Set up sharing with doctors, family members, or coaches. Control what information is visible to each person. Share specific records or entire profile sections.',
      },
      {
        number: 5,
        title: 'Track Daily Observations',
        description: 'Use Wellness Coach to document daily symptoms, medication adherence, mood changes, or other observations. Coach helps you track patterns over time.',
      },
    ],
    examples: [
      {
        title: 'Example Workflow',
        conversation: [
          'You: "I need to help my mom manage her diabetes. She takes metformin and checks her blood sugar daily"',
          '[Switch to mom\'s profile]',
          'Coach: "I\'ll help you set up her health profile. Let\'s start by adding her condition and medications."',
          'You: "She has type 2 diabetes and takes metformin 500mg twice daily"',
          'Coach: "I\'ve added that. Would you like me to create a goal to track her blood sugar readings and set up reminders for medication?"',
        ],
      },
    ],
    order: 1,
  },
  {
    id: 'medication-tracking',
    title: 'Track Medications and Symptom Patterns',
    description: 'Monitor medication adherence, side effects, and symptom changes to identify patterns',
    icon: FiCalendar,
    category: 'caregiver',
    whyMatters: 'Caregivers often need to track medications, symptoms, and side effects to help healthcare providers make informed decisions. This workflow helps you document everything systematically and spot patterns that might not be obvious day-to-day.',
    imagePlaceholder: 'Medication tracking',
    steps: [
      {
        number: 1,
        title: 'Add Medications to Profile',
        description: 'In their profile, use Wellness Coach to add all medications with dosages, frequencies, and start dates. Coach checks for interactions and genetic factors.',
      },
      {
        number: 2,
        title: 'Set Up Medication Reminders',
        description: 'Create reminders for medication times. Track adherence by noting when medications are taken or missed.',
      },
      {
        number: 3,
        title: 'Document Symptoms Daily',
        description: 'Use Wellness Coach for regular check-ins. Document symptoms, side effects, mood, energy levels, and any changes. Coach helps you track patterns.',
      },
      {
        number: 4,
        title: 'Identify Patterns',
        description: 'Ask Wellness Coach: "Are there patterns in my mom\'s symptoms related to her medications?" or "How do her symptoms correlate with medication timing?"',
      },
      {
        number: 5,
        title: 'Prepare Reports for Healthcare Providers',
        description: 'Generate summaries with medication adherence, symptom patterns, and observations. Share with doctors to help them make treatment decisions.',
      },
    ],
    examples: [
      {
        title: 'Example Conversation',
        conversation: [
          'You: "My dad has been more tired lately. He takes blood pressure medication in the morning"',
          'Coach: "Let\'s track this. When did you first notice the increased fatigue? Is it worse at certain times of day?"',
          'You: "It started about 2 weeks ago, and it\'s worse in the afternoon"',
          'Coach: "I\'ve documented this. Given the timing, it might be related to his medication. Would you like me to prepare a summary for his next doctor visit?"',
        ],
      },
    ],
    order: 2,
  },
  {
    id: 'family-coordination',
    title: 'Coordinate Care with Family and Healthcare Providers',
    description: 'Share health information with family members and healthcare providers while maintaining privacy',
    icon: FiShare2,
    category: 'caregiver',
    whyMatters: 'Caregiving often involves coordinating with multiple people — family members, doctors, specialists. Livewello makes it easy to share the right information with the right people while keeping everything organized and private.',
    imagePlaceholder: 'Family coordination',
    steps: [
      {
        number: 1,
        title: 'Set Up Sharing',
        description: 'Invite family members or healthcare providers to view specific profiles. Control what each person can see — full access, read-only, or specific sections only.',
      },
      {
        number: 2,
        title: 'Share Health Summaries',
        description: 'Generate comprehensive health summaries for doctor visits. Include current conditions, medications, recent labs, and observations. Share via email or print.',
      },
      {
        number: 3,
        title: 'Coordinate Appointments',
        description: 'Document appointment dates, questions to ask, and notes from visits. Share appointment summaries with family members who need to stay informed.',
      },
      {
        number: 4,
        title: 'Track Communication',
        description: 'Use Wellness Coach to document conversations with healthcare providers, medication changes, and treatment plans. Keep a record of all important communications.',
      },
      {
        number: 5,
        title: 'Maintain Privacy',
        description: 'Adjust sharing permissions as needed. Some family members might only need basic updates, while healthcare providers need full access to relevant records.',
      },
    ],
    examples: [
      {
        title: 'Example Workflow',
        conversation: [
          'You: "I need to share my mom\'s health information with her cardiologist and my sister"',
          'Coach: "I can help you set that up. What information should each person have access to?"',
          'You: "The cardiologist needs everything, but my sister just needs to see appointments and major changes"',
          'Coach: "I\'ve configured the sharing. The cardiologist has full access, and your sister will receive updates on appointments and significant health changes."',
        ],
      },
    ],
    order: 3,
  },
  // Coach use cases
  {
    id: 'client-management',
    title: 'Understand Client History and Health Profile',
    description: 'Access comprehensive client health records to understand their background and current status',
    icon: FiFileText,
    category: 'coach',
    whyMatters: 'Effective coaching requires understanding a client\'s complete health picture. Livewello gives you access to organized health records, genetic insights, and progress history — all controlled by your client.',
    imagePlaceholder: 'Client health profile',
    steps: [
      {
        number: 1,
        title: 'Client Shares Their Profile',
        description: 'Your client invites you to view their Livewello profile. They control what you can see — full access or specific sections like goals, conditions, or lab results.',
      },
      {
        number: 2,
        title: 'Review Health History',
        description: 'Access their health records: conditions, medications, supplements, allergies, genetic insights, and lab results. Everything is organized and easy to navigate.',
      },
      {
        number: 3,
        title: 'Review Progress Notes',
        description: 'Read Wellness Coach conversation history and progress notes to understand their journey, challenges, and what has worked previously.',
      },
      {
        number: 4,
        title: 'Get generated summaries',
        description: 'Ask Wellness Coach to generate summaries: "Give me a summary of this client\'s health profile" or "What genetic factors should I consider for this client?"',
      },
      {
        number: 5,
        title: 'Stay Updated',
        description: 'Receive notifications when clients update their health records, complete goals, or add new information relevant to your coaching.',
      },
    ],
    examples: [
      {
        title: 'Example Workflow',
        conversation: [
          'You: [Reviewing client profile]',
          'Coach: "I can help you understand this client\'s profile. They have MTHFR variants that affect folate metabolism, and they\'re currently tracking a sleep improvement goal."',
          'You: "What supplements would be most beneficial given their genetics?"',
          'Coach: "Based on their MTHFR variants, methylated B vitamins would be more effective. They also have variants affecting vitamin D metabolism."',
        ],
      },
    ],
    order: 1,
  },
  {
    id: 'progress-tracking',
    title: 'Track Client Progress and Measure Outcomes',
    description: 'Monitor goal completion, compliance, and health improvements to demonstrate your impact',
    icon: FiTrendingUp,
    category: 'coach',
    whyMatters: 'Tracking progress is essential for effective coaching and demonstrating value. Livewello gives you visibility into client goals, compliance, and outcomes — helping you adjust strategies and showcase results.',
    imagePlaceholder: 'Progress tracking dashboard',
    steps: [
      {
        number: 1,
        title: 'Review Client Goals',
        description: 'View all active wellness goals your client is working on. See target metrics, timelines, and current progress.',
      },
      {
        number: 2,
        title: 'Monitor Compliance',
        description: 'Track medication adherence, supplement compliance, and goal check-ins. See patterns in engagement and identify where clients need support.',
      },
      {
        number: 3,
        title: 'Review Progress Notes',
        description: 'Read Wellness Coach check-ins to understand client challenges, wins, and what strategies are working. Coach documents progress automatically.',
      },
      {
        number: 4,
        title: 'Analyze Trends',
        description: 'View lab result trends, symptom changes, and goal progress over time. Identify what interventions are most effective.',
      },
      {
        number: 5,
        title: 'Generate Progress Reports',
        description: 'Create reports showing client improvements, goal completions, and outcomes. Use these to demonstrate your impact and adjust coaching strategies.',
      },
    ],
    examples: [
      {
        title: 'Example Workflow',
        conversation: [
          'You: [Reviewing client progress]',
          'Coach: "This client has made significant progress on their sleep goal. They\'ve improved from 5.5 hours to 7 hours average sleep per night over the past 8 weeks."',
          'You: "What factors contributed to this improvement?"',
          'Coach: "Based on their progress notes, implementing a consistent bedtime routine and addressing their magnesium deficiency (related to their genetic variants) were key factors."',
        ],
      },
    ],
    order: 2,
  },
  {
    id: 'client-communication',
    title: 'Work with Clients Between Sessions',
    description: 'Use shared health records and personalized insights to support clients outside of scheduled sessions',
    icon: FiMessageSquare,
    category: 'coach',
    whyMatters: 'Effective coaching extends beyond scheduled sessions. Livewello provides a platform for ongoing support, allowing you to review client updates, provide guidance, and stay aligned with their goals between meetings.',
    imagePlaceholder: 'Client communication',
    steps: [
      {
        number: 1,
        title: 'Review Client Updates',
        description: 'Check for new health records, goal progress, or Wellness Coach notes. Stay informed about what\'s happening between sessions.',
      },
      {
        number: 2,
        title: 'Provide Guidance via Notes',
        description: 'Ask clients to share specific questions or concerns in Wellness Coach. Review their notes and provide guidance that gets saved to their records.',
      },
      {
        number: 3,
        title: 'Prepare for Sessions',
        description: 'Before each session, review recent updates, progress on goals, and any new health information. Come prepared with insights and recommendations.',
      },
      {
        number: 4,
        title: 'Suggest Goal Adjustments',
        description: 'Based on progress, suggest goal modifications, new goals, or strategy changes. Clients can implement these through Wellness Coach.',
      },
      {
        number: 5,
        title: 'Celebrate Wins',
        description: 'Acknowledge client achievements and progress. Use Wellness Coach to document wins and maintain motivation.',
      },
    ],
    examples: [
      {
        title: 'Example Workflow',
        conversation: [
          'You: [Reviewing client updates before session]',
          'Coach: "Your client completed their nutrition goal and uploaded new lab results showing improved B12 levels."',
          'You: "Great progress! I\'ll acknowledge this in our session and suggest a new goal focused on maintaining these improvements."',
        ],
      },
    ],
    order: 3,
  },
  {
    id: 'outcome-measurement',
    title: 'Measure Outcomes and Demonstrate Impact',
    description: 'Track client improvements, goal completions, and health outcomes to showcase your effectiveness',
    icon: FiBarChart,
    category: 'coach',
    whyMatters: 'Demonstrating impact is crucial for building your practice and helping clients see their progress. Livewello provides data-driven insights into client outcomes, helping you showcase results and improve your coaching effectiveness.',
    imagePlaceholder: 'Outcome measurement dashboard',
    steps: [
      {
        number: 1,
        title: 'Track Baseline Metrics',
        description: 'When clients start working with you, document baseline health metrics, lab values, symptoms, and goals. This becomes your starting point.',
      },
      {
        number: 2,
        title: 'Monitor Progress Over Time',
        description: 'Track improvements in lab results, symptom reduction, goal completions, and quality of life measures. See trends and patterns.',
      },
      {
        number: 3,
        title: 'Generate Outcome Reports',
        description: 'Create reports showing client improvements: "Client improved sleep from 5.5 to 7 hours average" or "B12 levels increased from 250 to 450 pg/mL".',
      },
      {
        number: 4,
        title: 'Identify Success Factors',
        description: 'Analyze what interventions worked best. Review genetic factors, supplement protocols, and lifestyle changes that led to improvements.',
      },
      {
        number: 5,
        title: 'Showcase Your Impact',
        description: 'Use outcome data (with client permission) to demonstrate your effectiveness, improve your coaching strategies, and attract new clients.',
      },
    ],
    examples: [
      {
        title: 'Example Workflow',
        conversation: [
          'You: "Show me outcomes for my clients over the past 6 months"',
          'Coach: "Here\'s a summary: 85% of clients completed their primary wellness goals. Average improvements: Sleep +1.5 hours, Energy levels +40%, Lab markers improved in 78% of cases."',
          'You: "What were the most effective interventions?"',
          'Coach: "Clients with genetic-informed supplement protocols showed 2x improvement rates. Sleep hygiene interventions were most effective for clients with CLOCK gene variants."',
        ],
      },
    ],
    order: 4,
  },
]

export const getUseCaseById = (id: string): UseCase | undefined => {
  return useCases.find((uc) => uc.id === id)
}

export const getUseCaseByOrder = (order: number, category?: string): UseCase | undefined => {
  const filtered = category ? useCases.filter((uc) => uc.category === category) : useCases
  return filtered.find((uc) => uc.order === order)
}

export const getUseCasesByCategory = (category: string): UseCase[] => {
  return useCases.filter((uc) => uc.category === category).sort((a, b) => a.order - b.order)
}

export const getNextUseCase = (currentId: string, category?: string): UseCase | undefined => {
  const current = getUseCaseById(currentId)
  if (!current) return undefined
  const filtered = category ? useCases.filter((uc) => uc.category === category) : useCases
  const currentIndex = filtered.findIndex((uc) => uc.id === currentId)
  if (currentIndex === -1 || currentIndex === filtered.length - 1) return undefined
  return filtered[currentIndex + 1]
}

export const getPrevUseCase = (currentId: string, category?: string): UseCase | undefined => {
  const current = getUseCaseById(currentId)
  if (!current) return undefined
  const filtered = category ? useCases.filter((uc) => uc.category === category) : useCases
  const currentIndex = filtered.findIndex((uc) => uc.id === currentId)
  if (currentIndex <= 0) return undefined
  return filtered[currentIndex - 1]
}

